import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

/*
 * Islam Magnet — panier rapide.
 * Chaque action (quantité, retirer, ajout depuis « Complétez votre collection », assurance)
 * est calculée d’avance puis envoyée à Shopify en UN SEUL appel :
 * - 1 présentoir offert (produit à 0 €) pour 2 magnets ;
 * - 1 ticket Omra par magnet ;
 * - assurance livraison : 0 ou 1.
 * Le bouton de paiement n’est jamais bloqué.
 */

const SECTION_ID = 'cart-drawer-section';
const ERROR = {
  fr: 'Le panier n’a pas pu être mis à jour. Réessayez.',
  en: 'The cart could not be updated. Please try again.'
};

function parseJSON(value, fallback) {
  try { return JSON.parse(value); } catch { return fallback; }
}

/**
 * Pure function. Given the cart and one optional action, returns the `updates`
 * object for /cart/update.js (keys = line keys or variant IDs, values = quantities).
 */
export function planCart(cart, config, action = null) {
  const magnetIds = new Set(config.magnetProductIds.map(Number));
  const extras = new Set([config.standVariantId, config.insuranceVariantId, config.ticketVariantId].filter(Boolean));
  const lines = (cart.items || []).map(item => ({ key: item.key, variant: Number(item.variant_id ?? item.id), product: Number(item.product_id), quantity: item.quantity }));
  const updates = {};

  // 1. Action de la cliente.
  if (action?.kind === 'quantity') {
    const line = lines.find(item => item.key === action.key);
    if (line) { line.quantity = action.quantity; updates[line.key] = action.quantity; }
  } else if (action?.kind === 'add') {
    const line = lines.find(item => item.variant === action.variantId && !extras.has(item.variant));
    if (line) { line.quantity += action.quantity; updates[line.key] = line.quantity; }
    else { lines.push({ key: null, variant: action.variantId, product: action.productId, quantity: action.quantity }); updates[action.variantId] = action.quantity; }
  }

  // 2. Règles automatiques, recalculées sur le panier « après action ».
  const magnetCount = lines.reduce((sum, item) => sum + (magnetIds.has(item.product) ? item.quantity : 0), 0);
  const hasGoods = lines.some(item => item.quantity > 0 && !extras.has(item.variant));
  const currentInsurance = lines.some(item => item.variant === config.insuranceVariantId && item.quantity > 0);
  const wantsInsurance = hasGoods && (action?.kind === 'insurance' ? action.checked : currentInsurance);

  const targets = [
    [config.standVariantId, Math.floor(magnetCount / 2)],
    [config.ticketVariantId, magnetCount],
    [config.insuranceVariantId, wantsInsurance ? 1 : 0]
  ];
  for (const [variant, wanted] of targets) {
    if (!variant) continue;
    const existing = lines.filter(item => item.variant === variant);
    if (!existing.length) {
      if (wanted > 0) updates[variant] = wanted;
      continue;
    }
    existing.forEach((item, index) => {
      const quantity = index === 0 ? wanted : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
  }
  return { updates, magnetCount };
}

class CartExtras {
  constructor(config) {
    this.config = {
      ...config,
      magnetProductIds: (config.magnetProductIds || []).map(Number),
      standVariantId: Number(config.standVariantId) || 0,
      insuranceVariantId: Number(config.insuranceVariantId) || 0,
      ticketVariantId: Number(config.ticketVariantId) || 0
    };
    this.queue = [];
    this.external = new Set();
    this.running = false;
    this.cart = this.readState(this.root?.querySelector('[data-im-cart-state]'));
    this.listen();
    this.schedule();
  }

  get root() { return document.querySelector('#cart-drawer'); }
  get lang() { return this.root?.querySelector('dialog')?.lang?.startsWith('en') ? 'en' : 'fr'; }

  /* ---------- Événements ---------- */

  listen() {
    // Mises à jour venues du thème ou du bloc d’offres : on récupère le panier, puis on applique les règles.
    const onExternalUpdate = event => {
      if (event.context === 'im-cart-extras') return;
      const pending = Promise.resolve(event.promise)
        .then(result => { this.cart = this.fromSections(result?.detail) || null; })
        .catch(() => { this.cart = null; });
      this.external.add(pending);
      pending.finally(() => { this.external.delete(pending); this.schedule(); });
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, onExternalUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, onExternalUpdate);

    document.addEventListener('quantity-selector:update', event => {
      const row = this.rowOf(event.target);
      const quantity = Number(event.detail?.quantity);
      if (!row || !Number.isInteger(quantity) || quantity < 0) return;
      event.stopImmediatePropagation();
      this.enqueue({ kind: 'quantity', key: row.dataset.key, quantity });
    }, true);

    document.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;

      // « − » sur 1 = retirer (on lit la vraie quantité du panier).
      const minus = event.target.closest('button[name="minus"]');
      const minusRow = this.rowOf(minus);
      if (minusRow) {
        const line = this.cart?.items?.find(item => item.key === minusRow.dataset.key);
        const input = minusRow.querySelector('input[name="updates[]"]');
        const current = line ? line.quantity : Number(input?.getAttribute('value') ?? input?.value);
        if (current <= 1) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.enqueue({ kind: 'quantity', key: minusRow.dataset.key, quantity: 0 });
        }
        return;
      }

      const remove = event.target.closest('.cart-items__remove');
      const removeRow = this.rowOf(remove);
      if (removeRow && !remove.disabled && !remove.hidden) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.enqueue({ kind: 'quantity', key: removeRow.dataset.key, quantity: 0 });
        return;
      }

      if (event.target.closest('#cart-drawer [data-im-clear]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openClearDialog();
        return;
      }

      const clearDialog = event.target.closest('#cart-drawer [data-im-clear-dialog]');
      if (clearDialog) {
        if (event.target.closest('[data-im-clear-cancel]') || event.target === clearDialog) {
          clearDialog.close();
          return;
        }
        if (event.target.closest('[data-im-clear-confirm]')) {
          clearDialog.close();
          this.enqueue({ kind: 'clear' });
          return;
        }
      }

      if (event.target.closest('#cart-drawer .im-cart-checkout')) this.goToCheckout(event);
    }, true);

    // « Complétez votre collection » : ajout direct, en un seul appel, puis remontée en haut.
    // Écouté sur window pour passer avant le thème.
    const addFromSuggestion = (event, form) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      const variantId = Number(form.querySelector('input[name="id"]')?.value);
      const productId = Number(form.closest('product-form-component')?.dataset.productId);
      if (!variantId) return;
      this.scrollTopNext = true;
      this.enqueue({ kind: 'add', variantId, productId, quantity: 1 });
    };
    window.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('#cart-drawer .im-cart-suggestion__add');
      const form = button?.closest('form');
      if (form) addFromSuggestion(event, form);
    }, true);
    window.addEventListener('submit', event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.id === 'cart-form' && event.submitter?.classList.contains('im-cart-checkout')) return this.goToCheckout(event);
      if (form.matches('#cart-drawer .im-cart-suggestion__form')) addFromSuggestion(event, form);
    }, true);

    document.addEventListener('change', event => {
      if (!(event.target instanceof HTMLInputElement) || !event.target.matches('#cart-drawer [data-im-insurance]')) return;
      event.stopImmediatePropagation();
      this.enqueue({ kind: 'insurance', checked: event.target.checked });
    }, true);

    window.addEventListener('pageshow', event => {
      this.root?.querySelector('.im-cart-checkout')?.classList.remove('is-loading');
      if (event.persisted) { this.cart = null; this.schedule(); }
    });
  }

  openClearDialog() {
    const root = this.root;
    if (!root) return;
    let dialog = root.querySelector('[data-im-clear-dialog]');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'im-clear-dialog';
      dialog.setAttribute('data-im-clear-dialog', '');
      dialog.setAttribute('aria-labelledby', 'im-clear-dialog-title');
      dialog.innerHTML = `
        <div class="im-clear-dialog__panel">
          <h2 id="im-clear-dialog-title">${this.lang === 'en' ? 'Empty your cart?' : 'Vider votre panier ?'}</h2>
          <p>${this.lang === 'en' ? 'All items will be removed from your cart.' : 'Tous les articles seront retirés de votre panier.'}</p>
          <div class="im-clear-dialog__actions">
            <button type="button" class="im-clear-dialog__cancel" data-im-clear-cancel>${this.lang === 'en' ? 'Cancel' : 'Annuler'}</button>
            <button type="button" class="im-clear-dialog__confirm" data-im-clear-confirm>${this.lang === 'en' ? 'Remove all' : 'Tout retirer'}</button>
          </div>
        </div>`;
      const style = document.createElement('style');
      style.textContent = `
        #cart-drawer .im-clear-dialog { width: min(calc(100% - 32px), 380px); max-width: none; max-height: calc(100% - 32px); padding: 0; border: 0; border-radius: 12px; background: transparent; color: #111; overflow: visible; }
        #cart-drawer .im-clear-dialog::backdrop { background: rgb(0 0 0 / 45%); }
        #cart-drawer .im-clear-dialog__panel { padding: 22px; border-radius: 12px; background: #fff; box-shadow: 0 12px 40px rgb(0 0 0 / 22%); font-family: inherit; }
        #cart-drawer .im-clear-dialog__panel h2 { margin: 0 0 8px; color: #111; font-size: 20px; font-weight: 800; line-height: 1.25; }
        #cart-drawer .im-clear-dialog__panel p { margin: 0; color: #555; font-size: 15px; line-height: 1.45; }
        #cart-drawer .im-clear-dialog__actions { display: flex; gap: 10px; margin-top: 22px; }
        #cart-drawer .im-clear-dialog__actions button { flex: 1; min-height: 44px; padding: 10px 12px; border: 1px solid #111; border-radius: 8px; font: inherit; font-size: 14px; font-weight: 700; cursor: pointer; }
        #cart-drawer .im-clear-dialog__cancel { background: #fff; color: #111; }
        #cart-drawer .im-clear-dialog__confirm { background: #111; color: #fff; }
      `;
      root.append(style, dialog);
    }
    if (!dialog.open) dialog.showModal();
    dialog.querySelector('[data-im-clear-cancel]')?.focus();
  }

  rowOf(target) {
    if (!(target instanceof Element)) return null;
    const row = target.closest('.cart-items__table-row[data-key]');
    return row && this.root?.contains(row) ? row : null;
  }

  enqueue(action) {
    if (action.kind === 'quantity') this.queue = this.queue.filter(item => !(item.kind === 'quantity' && item.key === action.key));
    if (action.kind === 'insurance') this.queue = this.queue.filter(item => item.kind !== 'insurance');
    this.queue.push(action);
    this.schedule();
  }

  schedule() {
    this.pending = true;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.run(), 0);
  }

  async goToCheckout(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.root?.querySelector('.im-cart-checkout')?.classList.add('is-loading');
    const started = Date.now();
    while (this.running && Date.now() - started < 5000) await new Promise(resolve => setTimeout(resolve, 100));
    window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
  }

  /* ---------- Réseau ---------- */

  sectionIds() {
    const ids = [...document.querySelectorAll('cart-items-component[data-section-id]')].map(node => node.dataset.sectionId);
    return [...new Set([SECTION_ID, ...ids])].slice(0, 5);
  }

  async request(path, payload = {}) {
    const context = new URL(location.href);
    context.searchParams.delete('sections');
    context.searchParams.delete('section_id');
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: this.sectionIds().join(','), sections_url: context.pathname + context.search })
    });
    const result = await response.json();
    if (!response.ok || result.status >= 400 || result.errors) throw new Error(result.description || result.message || 'Cart update failed');
    const cart = this.fromSections(result);
    if (!cart) throw new Error('Unreadable cart response');
    this.cart = cart;
    return cart;
  }

  readState(node) {
    if (!node) return null;
    const cart = parseJSON(node.textContent, null);
    if (!cart || !Array.isArray(cart.items)) return null;
    cart.items = cart.items.map(item => ({ ...item, variant_id: item.variant_id ?? item.id }));
    return cart;
  }

  fromSections(result) {
    const html = result?.sections?.[SECTION_ID];
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const cart = this.readState(doc.querySelector('[data-im-cart-state]'));
    return cart ? { ...cart, sections: result.sections } : null;
  }

  /* ---------- Affichage ---------- */

  async commit(cart) {
    const html = cart.sections?.[SECTION_ID];
    if (!html) return;
    await morphSection(SECTION_ID, html, { mode: 'hydration' });
    const deferred = CartLinesUpdateEvent.createPromise();
    (this.root?.querySelector('cart-items-component') || document).dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'im-cart-extras', lines: [], promise: deferred.promise }));
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { source: 'im-cart-extras', sections: cart.sections, items: cart.items, didError: false }
    });
    if (this.scrollTopNext) {
      this.scrollTopNext = false;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      requestAnimationFrame(() => this.root?.querySelector('.cart-drawer__items')?.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' }));
    }
  }

  showError(message = '') {
    const node = this.root?.querySelector('[data-im-cart-extras-error]');
    if (!node) return;
    node.textContent = message;
    node.hidden = !message;
  }

  setSyncing(value) {
    this.root?.toggleAttribute('data-im-syncing', value);
  }

  /* ---------- Boucle principale ---------- */

  async run() {
    if (this.running || this.external.size || !this.root) return;
    this.running = true;
    this.setSyncing(true);
    try {
      while (this.pending || this.queue.length) {
        this.pending = false;
        if (!this.cart) this.cart = await this.request('cart/update.js', { updates: {} });
        const action = this.queue.shift() || null;
        if (action?.kind === 'clear') {
          const cart = await this.request('cart/clear.js');
          if (!this.queue.length && !this.external.size) await this.commit(cart);
          continue;
        }
        const { updates } = planCart(this.cart, this.config, action);
        if (Object.keys(updates).length) {
          const cart = await this.request('cart/update.js', { updates });
          if (!this.queue.length && !this.external.size) await this.commit(cart);
        } else if (action && this.cart.sections) {
          await this.commit(this.cart);
        }
        this.showError('');
      }
    } catch (error) {
      console.warn('[Islam Magnet cart]', error);
      this.queue.length = 0;
      this.pending = false;
      this.cart = null;
      try { await this.commit(await this.request('cart/update.js', { updates: {} })); } catch {}
      this.showError(ERROR[this.lang]);
    } finally {
      this.running = false;
      this.setSyncing(false);
      if ((this.pending || this.queue.length) && !this.external.size) this.schedule();
    }
  }
}

function openDrawerIfRequested() {
  let requested = false;
  try {
    requested = sessionStorage.getItem('im-open-cart') === '1';
    sessionStorage.removeItem('im-open-cart');
  } catch {}
  if (!requested) return;
  const drawer = document.querySelector('#cart-drawer');
  if (typeof drawer?.open === 'function') return drawer.open();
  if (typeof drawer?.showDialog === 'function') return drawer.showDialog();
  document.querySelector('[on\\:click*="cart-drawer/open"], [aria-controls="cart-drawer"]')?.click();
}

function initialize() {
  setTimeout(openDrawerIfRequested, 300);
  const node = document.querySelector('[data-im-cart-config]');
  if (!node || window.__imCartExtras) return;
  const config = parseJSON(node.textContent, null);
  if (!config) return console.warn('[Islam Magnet cart] invalid config');
  window.__imCartExtras = new CartExtras(config);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
}
